import { useAuth } from '@skyslope/auth-react';
import { useMutation } from '@tanstack/react-query';

import { getGlobalToken } from '../../auth/oktaConfig';
import { integrationsApiUrl } from '../../lib/environment-config';
import type { IntegrationKey, IntegrationEnablement } from './types';
import { getIntegrations } from './use-get-integrations';

type ToggleIntegrationPayload = {
  integrationType: IntegrationKey;
  switchToEnablement: Omit<IntegrationEnablement['userEnablement'], 'pending'>;
};

export function useToggleIntegration() {
  const { userContext } = useAuth();

  return useMutation({
    mutationFn: async ({ integrationType, switchToEnablement }: ToggleIntegrationPayload) => {
      if (!userContext?.id) throw new Error('User ID is required');

      const result = await getIntegrations(userContext.id);
      const userCurrentEnablement = result[integrationType]?.userEnablement;
      if (userCurrentEnablement === 'pending' || userCurrentEnablement === switchToEnablement) {
        return {
          ...result[integrationType],
          skippedToggle: true,
          switchToEnablement,
        };
      } else {
        const result = await toggleIntegration(integrationType, userContext.id);
        return {
          ...result,
          skippedToggle: false,
          switchToEnablement,
        };
      }
    },
  });
}

async function toggleIntegration(integration: IntegrationKey, userId: string): Promise<IntegrationEnablement> {
  const accessToken = await getGlobalToken();

  const result = await fetch(`${integrationsApiUrl}/users/${userId}/integrations`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ integration }),
  });

  return result.json();
}
